import {
    FC,
    FormEvent,
    ReactElement,
    useRef,
    useState,
} from 'react'

import { DateInput, FormActionButtons, Textarea, TextInput, TimeInput } from '../../../compositions'
import { ChangeLogEntry, EditChangeLogEntryFormData } from '../../../entities/ChangeLog/ChangeLog'
import { getCurrentAppVersion } from '../../../entities/ChangeLog/ChangeLogHelpers'
import {
    parseChangeLogEntryDateString,
    transformChangeLogEntryToEditChangeLogEntryFormData,
    transformDateAndTimeToChangeLogEntryDateString,
} from '../../../entities/ChangeLog/ChangeLogTransformers'
import { formatDate } from '../../../helpers/date'
import trans from '../../../helpers/trans'
import { isVersionGreaterThan } from '../../../helpers/version'
import { DateFormat } from '../../../types/dateFormatTypes'
import { ModalFormProps } from '../../../types/modalFormTypes'
import {
    EditChangeLogEntryFormErrors,
    validateEditChangeLogEntryFormData,
} from './validations/editChangeLogEntryFormValidation'

import './EditChangeLogEntryForm.scss'

interface EditChangeLogEntryFormProps extends ModalFormProps<EditChangeLogEntryFormData> {
    changeLogEntry: ChangeLogEntry
}

export const EditChangeLogEntryForm: FC<EditChangeLogEntryFormProps> = ({
    isLoading,
    changeLogEntry,
    onSubmit,
    onCancel,
}): ReactElement => {
    const [formData, setFormData] = useState<EditChangeLogEntryFormData>(
        transformChangeLogEntryToEditChangeLogEntryFormData(changeLogEntry),
    )
    const [formErrors, setFormErrors] = useState<EditChangeLogEntryFormErrors>({})
    const [dateInputValue, setDateInputValue] = useState<Date>(() => parseChangeLogEntryDateString(changeLogEntry.date))
    const [timeInputValue, setTimeInputValue] = useState<string>(() => formatDate(
        parseChangeLogEntryDateString(changeLogEntry.date), DateFormat.inputTime),
    )

    const formRef = useRef<HTMLFormElement>(null)

    const isUnreleasedVersion = isVersionGreaterThan(formData.version, getCurrentAppVersion())

    const handleChange = (key: keyof EditChangeLogEntryFormData) => (value: string): void => {
        setFormData({ ...formData, [key]: value })
    }

    const handleSubmit = (e: FormEvent<HTMLFormElement>): void => {
        e.preventDefault()

        const formDataToSubmit: EditChangeLogEntryFormData = {
            ...formData,
            date: transformDateAndTimeToChangeLogEntryDateString(dateInputValue, timeInputValue),
        }

        const [errors, hasErrors] = validateEditChangeLogEntryFormData(formDataToSubmit)
        setFormErrors(errors)

        if (! hasErrors) {
            setFormData(formDataToSubmit)
            onSubmit(formDataToSubmit)
        }
    }

    return (
        <form ref={formRef} onSubmit={handleSubmit} className="edit-change-log-entry-form">
            <div className="edit-change-log-entry-form__row">
                <div className="edit-change-log-entry-form__col">
                    <TextInput
                        label={trans('containers.forms.addChangeLogEntryForm.version')}
                        type="text"
                        value={formData.version}
                        error={formErrors.version}
                        onChange={handleChange('version')}
                    />
                    {! formErrors.version && isUnreleasedVersion && (
                        <p className="edit-change-log-entry-form__info">
                            {trans(
                                'containers.forms.addChangeLogEntryForm.futureVersionWarning',
                                { version: formData.version },
                            )}
                        </p>
                    )}
                </div>
            </div>
            <div className="edit-change-log-entry-form__row">
                <div className="edit-change-log-entry-form__col">
                    <DateInput
                        label={trans('containers.forms.addChangeLogEntryForm.date')}
                        value={dateInputValue}
                        onChange={setDateInputValue}
                    />
                </div>
                <div className="edit-change-log-entry-form__col">
                    <TimeInput
                        label={trans('containers.forms.addChangeLogEntryForm.time')}
                        value={timeInputValue}
                        onChange={setTimeInputValue}
                    />
                </div>
            </div>
            <div className="edit-change-log-entry-form__row">
                <div className="edit-change-log-entry-form__col">
                    <TextInput
                        label={trans('containers.forms.addChangeLogEntryForm.intro')}
                        type="text"
                        value={formData.intro}
                        error={formErrors.intro}
                        onChange={handleChange('intro')}
                    />
                </div>
            </div>
            <div className="edit-change-log-entry-form__row">
                <div className="edit-change-log-entry-form__col">
                    <Textarea
                        label={trans('containers.forms.addChangeLogEntryForm.changes')}
                        value={formData.changes}
                        error={formErrors.changes}
                        onChange={handleChange('changes')}
                    />
                </div>
            </div>
            <div className="edit-change-log-entry-form__row">
                <div className="edit-change-log-entry-form__col">
                    <Textarea
                        label={trans('containers.forms.addChangeLogEntryForm.popupMessage')}
                        value={formData.popupMessage}
                        error={formErrors.popupMessage}
                        onChange={handleChange('popupMessage')}
                    />
                </div>
            </div>
            <FormActionButtons
                isLoading={isLoading}
                disabled={! formData.version || ! formData.changes}
                submitText={trans('common.edit')}
                onCancelClick={onCancel}
            />
        </form>
    )
}
